import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  getProject, 
  updateProject, 
  getProjectFiles, 
  uploadFile, 
  downloadFile,
  deleteFile,
  getProjectMessages, 
  sendMessage,
  getProjectContracts,
  createContract,
  updateContract,
  createPaymentCheckout,
  getProjectPayments
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft,
  Upload,
  Download,
  Trash2,
  Send,
  FileText,
  FileIcon,
  Image,
  Film,
  File,
  Plus,
  Check,
  DollarSign,
  CreditCard,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getFileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) return <Image className="w-5 h-5 text-purple-400" />;
  if (contentType?.startsWith('video/')) return <Film className="w-5 h-5 text-pink-400" />;
  if (contentType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-400" />;
  return <File className="w-5 h-5 text-slate-400" />;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function ProjectDetail() {
  const { projectId } = useParams();
  const { isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageInput, setMessageInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [newContract, setNewContract] = useState({ title: '', content: '' });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAllData();
  }, [projectId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [projectRes, filesRes, messagesRes, contractsRes, paymentsRes] = await Promise.all([
        getProject(projectId),
        getProjectFiles(projectId),
        getProjectMessages(projectId),
        getProjectContracts(projectId),
        getProjectPayments(projectId)
      ]);
      setProject(projectRes.data);
      setFiles(filesRes.data);
      setMessages(messagesRes.data);
      setContracts(contractsRes.data);
      setPayments(paymentsRes.data);
    } catch (error) {
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      await uploadFile(projectId, file);
      toast.success('File uploaded successfully');
      const filesRes = await getProjectFiles(projectId);
      setFiles(filesRes.data);
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await downloadFile(file.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteFile(fileId);
      toast.success('File deleted');
      setFiles(files.filter(f => f.id !== fileId));
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim()) return;

    try {
      await sendMessage(projectId, messageInput);
      setMessageInput('');
      const messagesRes = await getProjectMessages(projectId);
      setMessages(messagesRes.data);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleUpdateProject = async (updates) => {
    try {
      const response = await updateProject(projectId, updates);
      setProject(response.data);
      toast.success('Project updated');
      setEditDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    try {
      await createContract(projectId, newContract);
      toast.success('Contract created');
      setContractDialogOpen(false);
      setNewContract({ title: '', content: '' });
      const contractsRes = await getProjectContracts(projectId);
      setContracts(contractsRes.data);
    } catch (error) {
      toast.error('Failed to create contract');
    }
  };

  const handleSignContract = async (contractId) => {
    try {
      await updateContract(contractId, { status: 'signed' });
      toast.success('Contract signed');
      const contractsRes = await getProjectContracts(projectId);
      setContracts(contractsRes.data);
    } catch (error) {
      toast.error('Failed to sign contract');
    }
  };

  const handleSendContract = async (contractId) => {
    try {
      await updateContract(contractId, { status: 'sent' });
      toast.success('Contract sent to client');
      const contractsRes = await getProjectContracts(projectId);
      setContracts(contractsRes.data);
    } catch (error) {
      toast.error('Failed to send contract');
    }
  };

  const handleMakePayment = async () => {
    if (!project.payment_amount || project.payment_amount <= 0) {
      toast.error('No payment amount set for this project');
      return;
    }
    
    setPaymentLoading(true);
    try {
      const response = await createPaymentCheckout({
        project_id: projectId,
        amount: project.payment_amount, // Still pass for validation, but backend uses DB value
        description: `Payment for ${project.name}`,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400">Project not found</p>
      </div>
    );
  }

  const basePath = isAdmin ? '/admin' : '/dashboard';

  return (
    <div className="space-y-6" data-testid="project-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Link to={`${basePath}/projects`} className="text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-white">{project.name}</h1>
          <p className="text-slate-400">{project.client_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`badge-${project.status}`}>
            {project.status.replace('_', ' ')}
          </Badge>
          <Badge className={`badge-${project.payment_status}`}>
            {project.payment_status}
          </Badge>
        </div>
      </div>

      {/* Quick Actions */}
      {isAdmin && (
        <Card className="bg-[#111827] border-white/10">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3">
              <Select 
                value={project.status} 
                onValueChange={(value) => handleUpdateProject({ status: value })}
              >
                <SelectTrigger className="w-48 bg-[#1E293B] border-slate-700 text-white" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-slate-700">
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="awaiting_approval">Awaiting Approval</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Select 
                value={project.payment_status} 
                onValueChange={(value) => handleUpdateProject({ payment_status: value })}
              >
                <SelectTrigger className="w-48 bg-[#1E293B] border-slate-700 text-white" data-testid="payment-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-slate-700">
                  <SelectItem value="pending">Payment Pending</SelectItem>
                  <SelectItem value="partial">Partial Payment</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>

              <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-white/10">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Set Amount
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#111827] border-white/10">
                  <DialogHeader>
                    <DialogTitle className="text-white">Set Payment Amount</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const amount = parseFloat(e.target.amount.value);
                    if (!isNaN(amount)) handleUpdateProject({ payment_amount: amount });
                  }}>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-300">Amount ($)</Label>
                        <Input
                          name="amount"
                          type="number"
                          step="0.01"
                          defaultValue={project.payment_amount || ''}
                          placeholder="0.00"
                          className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                        />
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="ghost" onClick={() => setEditDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-[#4A90D9] hover:bg-[#3A80C9]">
                          Save
                        </Button>
                      </div>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="files" className="space-y-6">
        <TabsList className="bg-[#1E293B] border border-white/10">
          <TabsTrigger value="files" className="data-[state=active]:bg-[#4A90D9]" data-testid="tab-files">
            Files ({files.length})
          </TabsTrigger>
          <TabsTrigger value="messages" className="data-[state=active]:bg-[#4A90D9]" data-testid="tab-messages">
            Messages ({messages.length})
          </TabsTrigger>
          <TabsTrigger value="contracts" className="data-[state=active]:bg-[#4A90D9]" data-testid="tab-contracts">
            Contracts ({contracts.length})
          </TabsTrigger>
          <TabsTrigger value="payment" className="data-[state=active]:bg-[#4A90D9]" data-testid="tab-payment">
            Payment
          </TabsTrigger>
        </TabsList>

        {/* Files Tab */}
        <TabsContent value="files">
          <Card className="bg-[#111827] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Files</CardTitle>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#4A90D9] hover:bg-[#3A80C9]"
                  data-testid="upload-file-btn"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload File
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {files.length === 0 ? (
                <div className="text-center py-12">
                  <FileIcon className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No files uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {files.map((file) => (
                    <div 
                      key={file.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                      data-testid={`file-${file.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {getFileIcon(file.content_type)}
                        <div>
                          <p className="text-white font-medium">{file.filename}</p>
                          <p className="text-xs text-slate-400">
                            {formatFileSize(file.size)} • Uploaded by {file.uploaded_by_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDownload(file)}
                          className="text-[#4A90D9] hover:text-[#38BDF8]"
                          data-testid={`download-${file.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteFile(file.id)}
                          className="text-red-400 hover:text-red-300"
                          data-testid={`delete-file-${file.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Messages Tab */}
        <TabsContent value="messages">
          <Card className="bg-[#111827] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Messages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-96 overflow-y-auto mb-4 space-y-4 p-4 bg-[#0B1120] rounded-lg">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-400">No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div 
                      key={msg.id}
                      className={`message-bubble ${msg.sender_role === (isAdmin ? 'admin' : 'client') ? 'sent' : 'received'}`}
                      data-testid={`message-${msg.id}`}
                    >
                      <p className="text-sm">{msg.content}</p>
                      <p className={`text-xs mt-1 ${msg.sender_role === (isAdmin ? 'admin' : 'client') ? 'text-white/60' : 'text-slate-500'}`}>
                        {msg.sender_name} • {new Date(msg.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
              
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <Input
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 bg-[#1E293B] border-slate-700 text-white"
                  data-testid="message-input"
                />
                <Button type="submit" className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="send-message-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contracts Tab */}
        <TabsContent value="contracts">
          <Card className="bg-[#111827] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Contracts</CardTitle>
              {isAdmin && (
                <Dialog open={contractDialogOpen} onOpenChange={setContractDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="create-contract-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      New Contract
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#111827] border-white/10 max-w-2xl">
                    <DialogHeader>
                      <DialogTitle className="text-white">Create Contract</DialogTitle>
                      <DialogDescription className="text-slate-400">
                        Create a new contract for this project.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateContract} className="space-y-4">
                      <div>
                        <Label className="text-slate-300">Title *</Label>
                        <Input
                          value={newContract.title}
                          onChange={(e) => setNewContract({ ...newContract, title: e.target.value })}
                          required
                          placeholder="Service Agreement"
                          className="mt-1 bg-[#1E293B] border-slate-700 text-white"
                          data-testid="contract-title"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Content *</Label>
                        <Textarea
                          value={newContract.content}
                          onChange={(e) => setNewContract({ ...newContract, content: e.target.value })}
                          required
                          placeholder="Contract terms and conditions..."
                          className="mt-1 bg-[#1E293B] border-slate-700 text-white min-h-[200px]"
                          data-testid="contract-content"
                        />
                      </div>
                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="ghost" onClick={() => setContractDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" className="bg-[#4A90D9] hover:bg-[#3A80C9]" data-testid="submit-contract">
                          Create Contract
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {contracts.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">No contracts yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contracts.map((contract) => (
                    <div 
                      key={contract.id}
                      className="p-4 rounded-lg bg-white/5 border border-white/10"
                      data-testid={`contract-${contract.id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-white font-medium">{contract.title}</h4>
                          <p className="text-xs text-slate-400">
                            Created {new Date(contract.created_at).toLocaleDateString()}
                            {contract.signed_at && ` • Signed ${new Date(contract.signed_at).toLocaleDateString()}`}
                          </p>
                        </div>
                        <Badge className={`badge-${contract.status}`}>
                          {contract.status}
                        </Badge>
                      </div>
                      <div className="contract-content text-sm text-slate-300 bg-[#0B1120] p-4 rounded mb-3 max-h-48 overflow-y-auto">
                        {contract.content}
                      </div>
                      <div className="flex justify-end gap-2">
                        {isAdmin && contract.status === 'draft' && (
                          <Button 
                            size="sm"
                            onClick={() => handleSendContract(contract.id)}
                            className="bg-[#4A90D9] hover:bg-[#3A80C9]"
                            data-testid={`send-contract-${contract.id}`}
                          >
                            Send to Client
                          </Button>
                        )}
                        {!isAdmin && contract.status === 'sent' && (
                          <Button 
                            size="sm"
                            onClick={() => handleSignContract(contract.id)}
                            className="bg-[#10B981] hover:bg-[#059669]"
                            data-testid={`sign-contract-${contract.id}`}
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Sign Contract
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Tab */}
        <TabsContent value="payment">
          <Card className="bg-[#111827] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Payment Summary */}
                <div className="p-6 rounded-lg bg-white/5 border border-white/10">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <p className="text-sm text-slate-400 mb-1">Amount Due</p>
                      <p className="text-3xl font-bold text-white">
                        {project.payment_amount ? `$${project.payment_amount.toFixed(2)}` : 'Not set'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`badge-${project.payment_status}`}>
                        {project.payment_status === 'paid' ? 'Paid' : 
                         project.payment_status === 'partial' ? 'Partial' : 'Pending'}
                      </Badge>
                      {!isAdmin && project.payment_status !== 'paid' && project.payment_amount > 0 && (
                        <Button 
                          onClick={handleMakePayment}
                          disabled={paymentLoading}
                          className="bg-[#10B981] hover:bg-[#059669]"
                          data-testid="pay-now-btn"
                        >
                          {paymentLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4 mr-2" />
                              Pay Now
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment History */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">Payment History</h3>
                  {payments.length === 0 ? (
                    <div className="text-center py-8">
                      <DollarSign className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No payments yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {payments.map((payment) => (
                        <div 
                          key={payment.id}
                          className="flex items-center justify-between p-4 rounded-lg bg-white/5"
                          data-testid={`payment-${payment.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              payment.payment_status === 'paid' ? 'bg-green-500/20' : 
                              payment.payment_status === 'pending' ? 'bg-yellow-500/20' : 'bg-red-500/20'
                            }`}>
                              {payment.payment_status === 'paid' ? (
                                <Check className="w-5 h-5 text-green-500" />
                              ) : payment.payment_status === 'pending' ? (
                                <Loader2 className="w-5 h-5 text-yellow-500" />
                              ) : (
                                <DollarSign className="w-5 h-5 text-red-500" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">${payment.amount.toFixed(2)} {payment.currency.toUpperCase()}</p>
                              <p className="text-xs text-slate-400">
                                {new Date(payment.created_at).toLocaleDateString()} at {new Date(payment.created_at).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                          <Badge className={
                            payment.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' :
                            payment.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            payment.payment_status === 'refunded' ? 'bg-blue-500/20 text-blue-400' :
                            'bg-red-500/20 text-red-400'
                          }>
                            {payment.payment_status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Stripe Test Info */}
                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                  <p className="text-sm text-blue-400">
                    <strong>Test Mode:</strong> Use card number <code className="bg-blue-500/20 px-1 rounded">4242 4242 4242 4242</code> with any future expiry date and any CVC.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
